import {makeAutoObservable, runInAction} from 'mobx';
import type {ComponentType, ReactNode} from 'react';

// Import modal components
import ConfirmationModal from '@/components/modals/ConfirmationModal';
import AlertModal from '@/components/modals/AlertModal';
import UnAuthenticatedUserModal from '@/components/modals/UnAuthenticatedUserModal';
import AboutPaymentOptionModal from '@/components/modals/AboutPaymentOptionModal';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

export interface ModalOptions {
    dismissible?: boolean;
    backdrop?: boolean;
    size?: ModalSize;
    persistent?: boolean;
    className?: string;
    overlayClassName?: string;
}

export interface ModalConfig {
    id: string;
    component: ComponentType<any>;
    props?: Record<string, any>;
    options?: ModalOptions;
    zIndex?: number;
    createdAt?: number;
}

export interface ConfirmationModalProps {
    title: string;
    message: string | ReactNode;
    confirmText?: string;
    cancelText?: string;
    variant?: 'default' | 'destructive' | 'success' | 'warning';
    onConfirm?: () => void | Promise<void>;
    onCancel?: () => void;
}

export interface AlertModalProps {
    title: string;
    message: string | ReactNode;
    type?: 'info' | 'success' | 'warning' | 'error';
    buttonText?: string;
    onClose?: () => void;
}

export class ModalStore {
    private activeModals = new Map<string, ModalConfig>();
    private baseZIndex = 1000;
    private modalCounter = 0;

    constructor() {
        makeAutoObservable(this);
    }

    // Computed values
    get hasActiveModals(): boolean {
        return this.activeModals.size > 0;
    }

    get topModal(): ModalConfig | null {
        if (this.activeModals.size === 0) return null;

        const modals = Array.from(this.activeModals.values());
        return modals.reduce((top, current) =>
            (current.zIndex || 0) > (top.zIndex || 0) ? current : top
        );
    }

    get allModals(): ModalConfig[] {
        return Array.from(this.activeModals.values()).sort((a, b) =>
            (a.zIndex || 0) - (b.zIndex || 0)
        );
    }

    get modalCount(): number {
        return this.activeModals.size;
    }

    isModalOpen(id: string): boolean {
        return this.activeModals.has(id);
    }

    getModal(id: string): ModalConfig | undefined {
        return this.activeModals.get(id);
    }

    // Actions
    openModal(config: Omit<ModalConfig, 'zIndex' | 'createdAt'>): string {
        const modalId = config.id || this.generateModalId();

        runInAction(() => {
            const modalConfig: ModalConfig = {
                ...config,
                id: modalId,
                zIndex: this.baseZIndex + this.modalCounter,
                createdAt: Date.now(),
                options: {
                    dismissible: true,
                    backdrop: true,
                    size: 'md',
                    persistent: false,
                    ...config.options,
                },
            };

            this.activeModals.set(modalId, modalConfig);
            this.modalCounter++;
        });

        return modalId;
    }

    closeModal(id: string): boolean {
        const existed = this.activeModals.has(id);

        runInAction(() => {
            this.activeModals.delete(id);
        });

        return existed;
    }

    closeAllModals(): void {
        runInAction(() => {
            this.activeModals.clear();
            this.modalCounter = 0;
        });
    }

    closeTopModal(): boolean {
        const topModal = this.topModal;
        if (topModal && topModal.options?.dismissible !== false) {
            return this.closeModal(topModal.id);
        }
        return false;
    }

    updateModalProps(id: string, props: Record<string, any>): boolean {
        const modal = this.activeModals.get(id);
        if (!modal) return false;

        runInAction(() => {
            modal.props = {...modal.props, ...props};
        });

        return true;
    }

    updateModalOptions(id: string, options: ModalOptions): boolean {
        const modal = this.activeModals.get(id);
        if (!modal) return false;

        runInAction(() => {
            modal.options = {...modal.options, ...options};
        });

        return true;
    }

    // Convenience methods for common modal types
    openConfirmation(config: ConfirmationModalProps & { id?: string }): string {
        const modalId = config.id || this.generateModalId('confirmation');

        return this.openModal({
            id: modalId,
            component: ConfirmationModal,
            props: config,
            options: {
                size: 'sm',
                persistent: true,
                dismissible: false,
            },
        });
    }

    openAlert(config: AlertModalProps & { id?: string }): string {
        const modalId = config.id || this.generateModalId('alert');

        return this.openModal({
            id: modalId,
            component: AlertModal,
            props: config,
            options: {
                size: 'sm',
                dismissible: true,
            },
        });
    }

    // Quick methods for existing modals
    openUnAuthenticatedUserModal(): string {
        return this.openModal({
            id: 'unauthenticated-user',
            component: UnAuthenticatedUserModal,
            options: {size: 'md'},
        });
    }

    openAboutPaymentOptionModal(paymentType?: string): string {
        return this.openModal({
            id: 'about-payment-option',
            component: AboutPaymentOptionModal,
            props: {paymentType},
            options: {size: 'lg'},
        });
    }

    // Keyboard event handling
    handleEscapeKey(): boolean {
        const topModal = this.topModal;
        if (topModal?.options?.dismissible !== false) {
            return this.closeTopModal();
        }
        return false;
    }

    // Background click handling
    handleBackdropClick(modalId: string): boolean {
        const modal = this.getModal(modalId);
        if (modal?.options?.backdrop !== false && modal?.options?.dismissible !== false) {
            return this.closeModal(modalId);
        }
        return false;
    }

    // Cleanup method
    reset(): void {
        this.closeAllModals();
    }

    // Debug helpers (no side-effects; safe to call from UI/tests)
    debugSnapshot(): {
        count: number;
        modals: Array<{ id: string; component: string; zIndex: number; options: ModalOptions }>;
    } {
        return {
            count: this.modalCount,
            modals: this.allModals.map((m) => ({
                id: m.id,
                component: m.component.name,
                zIndex: m.zIndex ?? 0,
                options: m.options ?? {},
            })),
        };
    }

    // Private helpers
    private generateModalId(prefix = 'modal'): string {
        return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
}

export default ModalStore;
